import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import ExperienceFragment from '../../ExperienceFragment';
import { CreditProtectionForm } from './CreditProtectionForm';
import { AuthorizedUserForm } from './AuthorizedUserForm';
import { CardDesignForm } from './CardDesignForm';
import { APP_VERIFICATION_EVENT, IApplicationVerificationElement } from '../ApplicationVerification';
import { BalanceTransfersForm } from './BalanceTransfersForm';
import { AcquisitionFlow, getAcquisitionFlow, getAppData, getProgramData } from '../../../utils/acquisition-utils';
import { useAcquisitionContext } from '../../../providers/AcquisitionProvider/AcquisitionProvider';
import SessionStorage from '../../../utils/SessionStorage';
import AcquisitionErrorToggleAuthorizedUserEvent from '../../../utils/custom-events/AcquisitionErrorToggleAuthorizedUserEvent';
import { checkOfferForCreditProtectionEligibility } from '../../CreditProtection/CreditProtection';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

interface IIncludeXFFormProps {
    fragmentPath: string;
    sectionTitle?: string;
    sectionType?: string;
    cqItemsOrder?: string[];
    cqItems?: any;
    isZoomedIn?: boolean;

    // Card Design
    defaultCardImagePath?: string;
    premiumPrompt?: string;

    // Credit Protection
    enrolledLabel?: string;
    creditProtectionStatement?: string;

    // Authorized User
    addUserLabel?: string;
    auFirstName?: string;
    auMiddleInitial?: string;
    auLastName?: string;
    auSpouseLabel?: string;
    auDobLabel?: string;

    // Balance Transfer
    receiveTransferLabel?: string;

    // Rush Processing
    addRushLabel?: string;
}

type TExperienceFragmentProps = MappedComponentProperties & {
    cqItems?: Record<string, unknown>;
    cqItemsOrder?: string[];
};

const ExperienceFragmentComponent: React.ComponentType<TExperienceFragmentProps> = ExperienceFragment;

const IncludeXFForm = forwardRef((props: IIncludeXFFormProps, ref: ForwardedRef<IApplicationVerificationElement>): JSX.Element => {
    IncludeXFForm.displayName = 'IncludeXFForm';
    const [showSection, setShowSection] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(false);

    const {
        isAuthorisedUserSectionValid,
        authorizedUserInfo,
        creditProtectionInfo,
        checkIsCreditProtectionValid,
        balanceTransferSelected,
        selectedCardId,
        setIsAuthorizedUserInfoSaved,
        setIsAuthorizedUserInEditMode,
        setHasFocusedCreditProtection,
    } = useAcquisitionContext();

    const handleClickEdit = async () => {
        const flow = getAcquisitionFlow();
        if (editMode === false) {
            setEditMode(true);
            if (props.sectionType === 'authorizedUser') {
                setIsAuthorizedUserInEditMode(true);
            }
        } else if (props.sectionType === 'authorizedUser' && isAuthorisedUserSectionValid) {
            const appData = await getAppData();
            appData.AuthorizedUser = authorizedUserInfo;
            
            if (flow === AcquisitionFlow.PHA) {
                await SessionStorage.setCipher('flowApplicationData', appData);
            } else if (flow === AcquisitionFlow.PA) {
                await SessionStorage.setCipher('preApprovalFullAppData', appData);
            } else {
                await SessionStorage.setCipher('prequalFullAppData', appData);
            }
            setEditMode(false);
            setIsAuthorizedUserInEditMode(false);
            setIsAuthorizedUserInfoSaved(true);

        } else if (props.sectionType === 'creditProtectionProgram' && checkIsCreditProtectionValid()) {
            const appData = await getAppData();
            if (flow === AcquisitionFlow.PA) {
                appData.CreditProtection = {
                    IsCreditProtectionSelected: creditProtectionInfo.CreditProtectionSelected,
                    CreditProtectionInitials: creditProtectionInfo.CreditProtectionSelected ? creditProtectionInfo.CreditProtectionInitials : '',
                };
                await SessionStorage.setCipher('preApprovalFullAppData', appData);
            } else if (flow === AcquisitionFlow.PHA) {
                appData.creditProtectionSelected = creditProtectionInfo.CreditProtectionSelected;
                appData.creditProtectionInitials = creditProtectionInfo.CreditProtectionSelected ? creditProtectionInfo.CreditProtectionInitials : '';
                SessionStorage.setObject('flowApplicationData', appData);
            } else {
                appData.CreditProtectionSelected = creditProtectionInfo.CreditProtectionSelected;
                appData.CreditProtectionInitials = creditProtectionInfo.CreditProtectionSelected ? creditProtectionInfo.CreditProtectionInitials : '';
                
                await SessionStorage.setCipher('prequalFullAppData', appData);
            }
            setEditMode(false);
            setHasFocusedCreditProtection(false);

        } else if (props.sectionType === 'balanceTransfer') {
            const appData = await getAppData();
            appData.IsBalanceTransferSelected = balanceTransferSelected;
            
            if (flow === AcquisitionFlow.PHA) {
                await SessionStorage.setCipher('flowApplicationData', appData);
            } else if (flow === AcquisitionFlow.PA) {
                await SessionStorage.setCipher('preApprovalFullAppData', appData);
            } else {
                await SessionStorage.setCipher('prequalFullAppData', appData);
            }
            setEditMode(false);

        } else if (props.sectionType === 'cardDesign') {
            const appData = await getAppData();
            appData[flow === AcquisitionFlow.PA ? 'SelectedProgramPlasticCode' : 'SelectedCardId'] = selectedCardId;
            await SessionStorage.setCipher(flow === AcquisitionFlow.PA ? 'preApprovalFullAppData' : 'prequalFullAppData', appData);
            
            if (flow === AcquisitionFlow.PA) {
                appData.SelectedProgramPlasticCode = selectedCardId;
                await SessionStorage.setCipher('preApprovalFullAppData', appData);
            } else if (flow === AcquisitionFlow.PHA) {
                appData.SelectedCardId = selectedCardId;
                appData.SelectedProgramPlasticCode = selectedCardId;
                SessionStorage.setObject('flowApplicationData', appData);
            } else {
                appData.SelectedCardId = selectedCardId;
                await SessionStorage.setCipher('prequalFullAppData', appData);
            }
            setEditMode(false);

        }

        if (props.sectionType !== 'authorizedUser') {
            window.dispatchEvent(new Event(`acquisitionErrorToggle-${props.sectionType}`));
        } else {
            // notify auth user if we are entering/exiting 'EDIT' to ensure correct state value
            window.dispatchEvent(new AcquisitionErrorToggleAuthorizedUserEvent({ isDispatchFromVerification: editMode ? false : true }))
        }

        setTimeout(() => {
            window.dispatchEvent(new Event(APP_VERIFICATION_EVENT));
        });
    };

    const getInfoView = (): JSX.Element => {
        switch (props.sectionType) {
            case 'cardDesign':
                return <CardDesignForm 
                    defaultCardImagePath={props.defaultCardImagePath} 
                    premiumPrompt={props.premiumPrompt}
                    isInEdit={editMode}
                />;
            case 'creditProtectionProgram':
                return (<CreditProtectionForm 
                    enrolledLabel={props.enrolledLabel} 
                    creditProtectionStatement={props.creditProtectionStatement}
                />);
            case 'authorizedUser':
                return (<AuthorizedUserForm
                    addUserLabel={props.addUserLabel}
                    auDobLabel={props.auDobLabel}
                    auFirstName={props.auFirstName}
                    auLastName={props.auLastName}
                    auMiddleInitial={props.auMiddleInitial}
                    auSpouseLabel={props.auSpouseLabel}
                />);
            case 'balanceTransfer':
                return <BalanceTransfersForm receiveTransferLabel={props.receiveTransferLabel} />;
            default:
                return <div></div>;
        }
    };

    const isShowSection = async (): Promise<boolean> => {
        const programData = await getProgramData();
        const cardData = SessionStorage.tryGetObject('cardInfo', []);
        if (!Object.keys(programData).length) {
            return true;
        }

        if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
            const IsAuthorizedUserEligible = programData?.card?.keyCardBenefits?.availableAncillaries?.some((anc: Record<string, unknown>): boolean => anc?.title === 'AUTHORIZED_USER');
            const IsBalanceTransferEligible = programData?.card?.keyCardBenefits?.availableAncillaries?.some((anc: Record<string, unknown>): boolean => anc?.title === 'BALANCE_TRANSFER');
            const IsCreditProtectionEligible = await checkOfferForCreditProtectionEligibility();

            if (props.sectionType === 'authorizedUser' && !IsAuthorizedUserEligible) {
                return false;
            }
            if (props.sectionType === 'balanceTransfer' && !IsBalanceTransferEligible) {
                return false;
            }
            if (props.sectionType === 'creditProtectionProgram' && !IsCreditProtectionEligible) {
                return false;
            }
            if (props.sectionType === 'cardDesign' && cardData.length <= 1) {
                return false;
            }
        } else {
            if (props.sectionType === 'authorizedUser' && !programData.AuthorizedUserEligible && !programData.IsAuthorizedUserEligible) {
                return false;
            }
            if (props.sectionType === 'balanceTransfer' && !programData.BalanceTransferFlagsInfo.IsProgramCheckboxFlagOn) {
                return false;
            }
            if (props.sectionType === 'creditProtectionProgram' && !(await checkOfferForCreditProtectionEligibility())) {
                return false;
            }
            if (props.sectionType === 'cardDesign' && cardData.length <= 1) {
                return false;
            }
        }

        return true;
    };

    useImperativeHandle(ref, () => {
        return {
            inEditMode: editMode,
        };
    }, [editMode]);

    useEffect(() => {
        isShowSection().then(isShow => {
            if (!isShow) {
                setShowSection(false);
            }
        });
    }, []);

    return showSection ? (
        <section className={`ApplicationVerification__formSection ${props.isZoomedIn ? 'ApplicationVerification__formSection--zoomed': ''}`}>
            <div className='ApplicationVerification__sectionHeading'>
                <h3 className='ApplicationVerification__sectionHeadline'>{props.sectionTitle}</h3>
                <div className='ApplicationVerification__toggleContainer'>
                    <a className='ApplicationVerification__editToggle topSection'
                        id={props.sectionType && `${props.sectionType}Edit`}
                        onClick={async () => await handleClickEdit()}>{editMode ? 'SAVE' : 'EDIT'}</a>
                </div>
            </div>
            <div className='PersonalInformation__sectionInfo'>
                <span className={`ExperienceFragment ${editMode ? 'show' : 'hide'}`}>
                    <ExperienceFragmentComponent cqPath={props.fragmentPath} cqItems={props.cqItems} cqItemsOrder={props.cqItemsOrder} isInEditor={false} />
                </span>
                <span key={editMode ? 'show' : 'hide'} className={`ExperienceFragment ${!editMode ? 'show' : 'hide'}`}>
                    {getInfoView()}
                </span>
            </div>
        </section>
    )
        : <React.Fragment></React.Fragment>;
});

export default IncludeXFForm;